import axios from "axios";
import { attachOfflineInterceptor } from "./offlineQueue";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  timeout: 120000,
});

// Demo-mode guard: block ALL writes when the fake demo session is active.
// A CustomEvent is fired so a top-level "sign up to continue" modal can react.
const DEMO_KEY = "hexa_demo";
const DEMO_WRITE_METHODS = new Set(["post", "put", "patch", "delete"]);
// A handful of endpoints are safe to call in demo (LLM chat, image gen, etc.)
// because they don't persist anything the demo user cares about. Everything
// else (files, meetings, projects…) is blocked.
const DEMO_ALLOW_WRITE_PATH = /^\/(ai\/chat|ai\/quick|ai\/image)/;

api.interceptors.request.use((config) => {
  const isDemo = typeof window !== "undefined" && localStorage.getItem(DEMO_KEY) === "1";
  const method = (config.method || "get").toLowerCase();
  const path = (config.url || "").replace(config.baseURL || "", "");
  if (isDemo && DEMO_WRITE_METHODS.has(method) && !DEMO_ALLOW_WRITE_PATH.test(path)) {
    // Block the request, tell the shell to prompt for signup.
    window.dispatchEvent(new CustomEvent("hexa:demo-prompt", { detail: { path, method } }));
    const err = new Error("DEMO_MODE_BLOCKED");
    err.isDemoBlocked = true;
    return Promise.reject(err);
  }
  return config;
});

// Capture failed mutating calls (offline / network error) into a replay queue.
attachOfflineInterceptor(api);

// ─────────────────────────────────────────────────────────────────
// Session expiry / 401 guard.
// When the JWT expires OR is invalid, every subsequent API call
// would otherwise pop a raw "Token expired" toast in whatever
// component made the request. Instead, wipe the stale token, tell
// the app shell to bounce to the login screen with a friendly
// "Session expired" flash, and swallow the noisy per-call error.
// ─────────────────────────────────────────────────────────────────
const TOKEN_KEY = "aura_token";
let _authGuardFired = false;

api.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err?.response?.status;
    const detail = String(err?.response?.data?.detail || "").toLowerCase();
    const looksLikeExpiry = status === 401 && (detail.includes("token") || detail.includes("expired") || detail.includes("not authenticated"));
    if (looksLikeExpiry && !_authGuardFired && typeof window !== "undefined") {
      _authGuardFired = true;
      try {
        // Clear stored auth so the app remounts as logged-out
        localStorage.removeItem(TOKEN_KEY);
        api.defaults.headers.common["Authorization"] = "";
      } catch (_) { /* ignore storage errors */ }
      // Broadcast so the shell can show a subtle "signed out" toast + route
      // to the login screen without the user seeing a raw error message.
      window.dispatchEvent(new CustomEvent("hexa:session-expired"));
      // Fall through — reject the promise so callers can still handle it,
      // but the shell will already have redirected by then.
    }
    return Promise.reject(err);
  }
);

export const aiChat = (session_id, message, system) =>
  api.post("/ai/chat", { session_id, message, system, persist: true }).then(r => r.data);

export const aiQuick = (prompt, system) =>
  api.post("/ai/quick", { prompt, system }).then(r => r.data);

export const aiImage = (prompt) =>
  api.post("/ai/image", { prompt }).then(r => r.data);

export const listImages = () => api.get("/ai/image/list").then(r => r.data);

export const getChatHistory = (sid) =>
  api.get(`/ai/chat/history/${sid}`).then(r => r.data);

export const clearChat = (sid) =>
  api.delete(`/ai/chat/history/${sid}`).then(r => r.data);

// notes
export const notesApi = {
  list: () => api.get("/notes").then(r => r.data),
  create: (n) => api.post("/notes", n).then(r => r.data),
  update: (id, n) => api.put(`/notes/${id}`, n).then(r => r.data),
  remove: (id) => api.delete(`/notes/${id}`).then(r => r.data),
};

// documents
export const docsApi = {
  list: (kind, crm_lead_id) => {
    const params = {};
    if (kind) params.kind = kind;
    if (crm_lead_id) params.crm_lead_id = crm_lead_id;
    return api.get("/documents", { params }).then(r => r.data);
  },
  create: (d) => api.post("/documents", d).then(r => r.data),
  update: (id, d) => api.put(`/documents/${id}`, d).then(r => r.data),
  remove: (id) => api.delete(`/documents/${id}`).then(r => r.data),
};

// slides / AI PPT maker
export const slidesApi = {
  generate: (payload) => api.post("/slides/ai/generate", payload).then(r => r.data),
  exportPptx: (payload) =>
    api.post("/slides/export/pptx", payload, { responseType: "blob" }).then(r => r.data),
  exportPdf: (payload) =>
    api.post("/slides/export/pdf", payload, { responseType: "blob" }).then(r => r.data),
};

// files (virtual)
export const filesApi = {
  list: (parent = null) => api.get("/files", { params: { parent } }).then(r => r.data),
  all: () => api.get("/files/all").then(r => r.data),
  create: (f) => api.post("/files", f).then(r => r.data),
  update: (id, f) => api.put(`/files/${id}`, f).then(r => r.data),
  remove: (id) => api.delete(`/files/${id}`).then(r => r.data),
  rename: (id, name) => api.patch(`/files/${id}/rename`, { name }).then(r => r.data),
  move: (id, parent) => api.patch(`/files/${id}/move`, { parent }).then(r => r.data),
  share: (id, address, perm = "read") =>
    api.post(`/files/${id}/share`, { address, perm }).then(r => r.data),
  unshare: (id, address) =>
    api.delete(`/files/${id}/share/${encodeURIComponent(address)}`).then(r => r.data),
  upload: (file, parent = null, onUploadProgress) => {
    const fd = new FormData();
    fd.append("upload", file, file.name);
    if (parent) fd.append("parent", parent);
    fd.append("name", file.name);
    return api
      .post("/files/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress,
        timeout: 180000,
      })
      .then((r) => r.data);
  },
  downloadUrl: (id) => {
    const token = localStorage.getItem("aura_token") || "";
    return `${API}/files/${id}/download?token=${encodeURIComponent(token)}`;
  },
};

// events
export const eventsApi = {
  list: () => api.get("/events").then(r => r.data),
  create: (e) => api.post("/events", e).then(r => r.data),
  remove: (id) => api.delete(`/events/${id}`).then(r => r.data),
};

// Day recap — summarize today's work & save / delete
export const dayRecapApi = {
  generate: (date) => api.post("/day-recap/generate", { date }).then(r => r.data),
  save: (payload) => api.post("/day-recap/save", payload).then(r => r.data),
  list: () => api.get("/day-recap").then(r => r.data),
  remove: (id) => api.delete(`/day-recap/${id}`).then(r => r.data),
  team: (date, ai = false) => api.post("/day-recap/team", { date, ai }).then(r => r.data),
};

// Projects (workspace-scoped) — boss creates, employees see only theirs.
export const projectsApi = {
  list: () => api.get("/projects").then(r => r.data),
  create: (payload) => api.post("/projects", payload).then(r => r.data),
  update: (id, payload) => api.put(`/projects/${id}`, payload).then(r => r.data),
  remove: (id) => api.delete(`/projects/${id}`).then(r => r.data),
  dashboard: () => api.get("/projects/dashboard").then(r => r.data),
};

// ============== AI-DRAFT APPROVAL WORKFLOW ==============
// Unified queue for AI-generated mail replies, social posts and DM replies.
export const approvalsApi = {
  list: (status = "pending") => api.get("/approvals", { params: { status } }).then(r => r.data),
  count: () => api.get("/approvals/count").then(r => r.data),
  edit: (id, draft_body) => api.put(`/approvals/${id}`, { draft_body }).then(r => r.data),
  approve: (id) => api.post(`/approvals/${id}/approve`).then(r => r.data),
  reject: (id) => api.post(`/approvals/${id}/reject`).then(r => r.data),
  regenerate: (id) => api.post(`/approvals/${id}/regenerate`).then(r => r.data),
};

export const voiceProfileApi = {
  get: () => api.get("/me/voice-profile").then(r => r.data),
  update: (patch) => api.put("/me/voice-profile", patch).then(r => r.data),
};

export const socialApi = {
  accounts: () => api.get("/social/accounts").then(r => r.data),
  posts: () => api.get("/social/posts").then(r => r.data),
  createPostManual: (platform, body) => api.post("/social/posts/manual", { platform, body }).then(r => r.data),
  draftPost: (platform, topic) => api.post("/social/posts/ai-draft", { platform, topic }).then(r => r.data),
  dms: () => api.get("/social/dms").then(r => r.data),
  dmThread: (id) => api.get(`/social/dms/${id}`).then(r => r.data),
  draftDmReply: (threadId) => api.post(`/social/dms/${threadId}/ai-draft`).then(r => r.data),
  simulateInbound: (threadId, body) => api.post("/social/dms/simulate-inbound", { thread_id: threadId, body }).then(r => r.data),
};

export const mailDraftApi = (msgId) =>
  api.post(`/mail/messages/${msgId}/ai-draft`).then(r => r.data);

// AI Email Intelligence (analyze / extract-tasks / schedule-meeting / voice-polish)
export const mailAiApi = {
  analyze: (msgId, force = false) =>
    api.post(`/mail/messages/${msgId}/analyze${force ? "?force=true" : ""}`).then(r => r.data),
  extractTasks: (msgId) =>
    api.post(`/mail/messages/${msgId}/extract-tasks`).then(r => r.data),
  scheduleMeeting: (msgId, body = {}) =>
    api.post(`/mail/messages/${msgId}/schedule-meeting`, body).then(r => r.data),
  voicePolish: (transcript, kind = "body") =>
    api.post(`/mail/ai/voice-polish`, { transcript, kind }).then(r => r.data),
};

// ============== ATTENDANCE / WORKFORCE ==============
export const attendanceApi = {
  clockIn: (opts = {}) => api.post("/attendance/clock-in", {
    note: opts.note || "",
    geo: opts.geo || null,
    tz_offset_minutes: (typeof opts.tz_offset_minutes === "number") ? opts.tz_offset_minutes : (-new Date().getTimezoneOffset()),
  }).then(r => r.data),
  clockOut: (opts = {}) => api.post("/attendance/clock-out", {
    note: opts.note || "",
    geo: opts.geo || null,
    tz_offset_minutes: (typeof opts.tz_offset_minutes === "number") ? opts.tz_offset_minutes : (-new Date().getTimezoneOffset()),
  }).then(r => r.data),
  breakStart: () => api.post("/attendance/break/start").then(r => r.data),
  breakEnd: () => api.post("/attendance/break/end").then(r => r.data),
  meToday: () => api.get("/attendance/me/today").then(r => r.data),
  meHistory: (days = 14) => api.get("/attendance/me", { params: { days } }).then(r => r.data),
  team: (date) => api.get("/attendance/team", { params: date ? { date } : {} }).then(r => r.data),
  teamSummary: (days = 7) => api.get("/attendance/team/summary", { params: { days } }).then(r => r.data),
  getPolicy: () => api.get("/attendance/policy").then(r => r.data),
  setPolicy: (patch) => api.put("/attendance/policy", patch).then(r => r.data),
  checkOverstays: () => api.post("/attendance/check-overstays").then(r => r.data),
  alerts: () => api.get("/attendance/alerts").then(r => r.data),
  // CSV download — returns full URL with embedded token (axios returns blob via direct route)
  exportUrl: (fromDate, toDate) => {
    const token = localStorage.getItem("aura_token") || "";
    const qs = new URLSearchParams({ ...(fromDate ? { from_date: fromDate } : {}), ...(toDate ? { to_date: toDate } : {}) }).toString();
    return { url: `${process.env.REACT_APP_BACKEND_URL}/api/attendance/export${qs ? "?" + qs : ""}`, token };
  },
};

export const teamApi = {
  list: () => api.get("/users/team").then(r => r.data),
  setRole: (address, role) => api.put(`/users/${address}/role`, { role }).then(r => r.data),
  createEmployee: (username, password, name = "") =>
    api.post("/users/employee", { username, password, name }).then(r => r.data),
};

// ============== EXTERNAL EMAIL (Gmail / Outlook / Custom IMAP) ==============
export const externalMailApi = {
  list: () => api.get("/external-mail/accounts").then(r => r.data),
  connect: (provider, email, app_password = "", display_name = "", imap_host = "", imap_port = 993) =>
    api.post("/external-mail/connect", { provider, email, app_password, display_name, imap_host, imap_port }).then(r => r.data),
  disconnect: (id) => api.delete(`/external-mail/accounts/${id}`).then(r => r.data),
  sync: (id) => api.post(`/external-mail/${id}/sync`).then(r => r.data),
  sendReply: (msgId, body, subject = "") =>
    api.post(`/external-mail/messages/${msgId}/send-reply`, { body, subject }).then(r => r.data),
  send: (external_account_id, to_addr, subject, body, attachments = []) =>
    api.post("/external-mail/send", { external_account_id, to_addr, subject, body, attachments }).then(r => r.data),
};

// Per-bucket unread counts (hexa vs external) for the signed-in user's inbox.
export const mailUnreadApi = {
  counts: () => api.get("/mail/unread-counts").then(r => r.data),
};

// ============== MEETINGS ==============
export const meetingsApi = {
  create: ({ title, scheduled_at, duration_min = 30, host, participants = [], agenda = "" }) =>
    api.post("/meetings", { title, scheduled_at, duration_min, host, participants, agenda }).then(r => r.data),
};

// ============== SOCIAL AD CAMPAIGNS ==============
export const campaignsApi = {
  list: () => api.get("/social/campaigns").then(r => r.data),
  create: (data) => api.post("/social/campaigns", data).then(r => r.data),
  update: (id, patch) => api.put(`/social/campaigns/${id}`, patch).then(r => r.data),
  remove: (id) => api.delete(`/social/campaigns/${id}`).then(r => r.data),
  aiDraft: (platform, product, target, objective = "engagement") =>
    api.post("/social/campaigns/ai-draft", { platform, product, target, objective }).then(r => r.data),
};

export const socialAuthApi = {
  connect: (platform, handle) =>
    api.post("/social/accounts/connect", { platform, handle }).then(r => r.data),
  disconnect: (platform) =>
    api.post("/social/accounts/disconnect", { platform, handle: "" }).then(r => r.data),
};

// ============== META (FACEBOOK / INSTAGRAM) ==============
export const metaApi = {
  getConfig: () => api.get("/meta/config").then(r => r.data),
  saveConfig: (app_id, app_secret) => api.put("/meta/config", { app_id, app_secret }).then(r => r.data),
  connect: (user_access_token) => api.post("/meta/connect", { user_access_token }).then(r => r.data),
  connections: () => api.get("/meta/connections").then(r => r.data),
  disconnect: () => api.delete("/meta/disconnect").then(r => r.data),
  fbPosts: (page_id) => api.get(`/meta/${page_id}/facebook/posts`).then(r => r.data),
  fbPublish: (page_id, message, image_url) => api.post(`/meta/${page_id}/facebook/publish`, { message, image_url }).then(r => r.data),
  igMedia: (page_id) => api.get(`/meta/${page_id}/instagram/media`).then(r => r.data),
  igPublish: (page_id, caption, image_url) => api.post(`/meta/${page_id}/instagram/publish`, { caption, image_url }).then(r => r.data),
};

// ============== TEAM CHAT (DMs + Spaces) ==============
export const teamChatApi = {
  listRooms: () => api.get("/chat/rooms").then(r => r.data),
  createDM: (address) => api.post("/chat/rooms/dm", { address }).then(r => r.data),
  createSpace: (name, members = []) =>
    api.post("/chat/rooms/space", { name, members }).then(r => r.data),
  addMembers: (room_id, members) =>
    api.post(`/chat/rooms/${room_id}/members`, { members }).then(r => r.data),
  messages: (room_id, limit = 100) =>
    api.get(`/chat/rooms/${room_id}/messages`, { params: { limit } }).then(r => r.data),
  send: (room_id, body, reply_to = null, attachments = []) =>
    api.post(`/chat/rooms/${room_id}/messages`, { body, reply_to, attachments }).then(r => r.data),
  edit: (msg_id, body) => api.put(`/chat/messages/${msg_id}`, { body }).then(r => r.data),
  remove: (msg_id) => api.delete(`/chat/messages/${msg_id}`).then(r => r.data),
  toggleReaction: (msg_id, emoji) =>
    api.post(`/chat/messages/${msg_id}/reactions`, { emoji }).then(r => r.data),
  markRead: (room_id) => api.post(`/chat/rooms/${room_id}/read`).then(r => r.data),
  aiAssist: (room_id, intent, text = "") =>
    api.post("/chat/ai", { room_id, intent, text }).then(r => r.data),
};

